import { useState } from "react";
import {
  Modal,
  Row,
  Col,
  Button,
  Typography,
  Card,
  Space,
  Tooltip,
  message,
  Tag,
  Input,
  Table,
  notification,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { IoCloseSharp } from "react-icons/io5";
import { useDispatch } from "react-redux";
import { actions } from "../slice";

const { Title, Text } = Typography;

function AddDevices({
  KitDetail,
  fetchKitDetail,
  setKitDataAfterAssetCreation,
  setIsOpenModalAfterAssetCreation,
  disabledActions,
}) {
  const dispatch = useDispatch();
  const [isAddDeviceModalVisible, setIsAddDeviceModalVisible] = useState(false);
  const [isDevicesAdding, setIsDevicesAdding] = useState(false);

  const [deviceState, setDeviceState] = useState({
    deviceName: "",
    deviceModel: "",
    uniqueId: "",
    description: "",
    devices: [],
  });

  const { deviceName, deviceModel, uniqueId, description, devices } =
    deviceState;

  const updateDeviceState = (updates) => {
    setDeviceState((prev) => ({ ...prev, ...updates }));
  };

  // Add device
  const handleAddDevice = () => {
    if (!deviceName.trim()) {
      message.error("Please enter device name");
      return;
    }
    if (!deviceModel.trim()) {
      message.error("Please enter device model");
      return;
    }
    if (!uniqueId.trim()) {
      message.error("Please enter unique ID");
      return;
    }
    // Check for duplicate unique ID
    const isDuplicate = devices.some((d) => d.uniqueId === uniqueId.trim());
    if (isDuplicate) {
      message.warning("Device with this Unique ID already exists");
      return;
    }
    const newDevice = {
      deviceName: deviceName.trim(),
      deviceModel: deviceModel.trim(),
      uniqueId: uniqueId.trim(),
      description: description.trim(),
    };
    updateDeviceState({
      devices: [...devices, newDevice],
      deviceName: "",
      deviceModel: "",
      uniqueId: "",
      description: "",
    });
  };

  // Delete device
  const handleDeleteDevice = (deviceUniqueId) => {
    updateDeviceState({
      devices: devices.filter((d) => d.uniqueId !== deviceUniqueId),
    });
  };

  const handleAddDevices = () => {
    if (devices.length === 0) {
      message.error("Please add at least one device");
      return;
    }

    const data = {
      devices: devices,
      samplerKitId: KitDetail._id,
    };
    setIsDevicesAdding(true);
    dispatch(
      actions.addDevice({
        data,
        onSuccess: (data) => {
          setKitDataAfterAssetCreation(data.data);
          resetState();
          setIsOpenModalAfterAssetCreation(true);
          fetchKitDetail();
        },
        onFailure: (error) => {
          setIsDevicesAdding(false);

          let errorMessage = "";
          if (error?.data?.failedDevices?.length === devices?.length) {
            errorMessage =
              "Oops, These devices already exist. Please use unique Device Name and Unique ID combinations";
          }
          notification.error({
            message: errorMessage || error.message,
          });
        },
      })
    );
  };

  const resetState = () => {
    setDeviceState({
      deviceName: "",
      deviceModel: "",
      uniqueId: "",
      description: "",
      devices: [],
    });
    setIsAddDeviceModalVisible(false);
    setIsDevicesAdding(false);
  };

  // Table columns
  const columns = [
    {
      title: "Device Name",
      dataIndex: "deviceName",
      key: "deviceName",
      width: "20%",
      render: (text) => (
        <Text style={{ fontWeight: 600, fontSize: 13, color: "#232A36" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Device Model",
      dataIndex: "deviceModel",
      key: "deviceModel",
      width: "20%",
      render: (text) => (
        <Text style={{ fontWeight: 600, fontSize: 13, color: "#232A36" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Unique ID",
      dataIndex: "uniqueId",
      key: "uniqueId",
      width: "20%",
      render: (text) => (
        <Text style={{ fontWeight: 600, fontSize: 13, color: "#232A36" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "25%",
    },
    {
      title: "Action",
      key: "action",
      width: "15%",
      align: "center",
      render: (_, record) => (
        <Tooltip title="Delete">
          <Button
            type="text"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteDevice(record.uniqueId)}
            style={{ color: "#E14D4F", fontSize: 16 }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <Tooltip
        title={
          disabledActions
            ? "Device can only be added when the kit is in the Pending or Packed stage"
            : "Add more devices to this kit"
        }
      >
        <Button
          type="primary"
          onClick={() => {
            setIsAddDeviceModalVisible(true);
          }}
          disabled={disabledActions}
        >
          Add More Devices
        </Button>
      </Tooltip>
      <Modal
        visible={isAddDeviceModalVisible}
        onCancel={resetState}
        title={
          <Title level={3} style={{ fontWeight: 800, margin: 0, padding: 0 }}>
            📱 Add More Devices
          </Title>
        }
        footer={
          <Space>
            <Button
              key="cancel"
              onClick={resetState}
              style={{
                borderRadius: 8,
                fontWeight: 700,
              }}
            >
              Cancel
            </Button>
            <Button
              key="submit"
              type="primary"
              loading={isDevicesAdding}
              onClick={handleAddDevices}
              disabled={devices.length === 0}
              style={{
                borderRadius: 8,
                fontWeight: 700,
              }}
            >
              Add Devices ({devices.length})
            </Button>
          </Space>
        }
        width={800}
        bodyStyle={{ borderRadius: 16, padding: 24, background: "#fff" }}
        style={{ borderRadius: 16 }}
        closeIcon={<IoCloseSharp size="24" />}
        centered
        className="modal-content"
      >
        {/* Device Input Fields */}
        <Row gutter={[12, 12]} style={{ marginBottom: 8 }}>
          <Col span={8}>
            <Text style={{ fontWeight: 600, color: "#232A36", fontSize: 13 }}>
              Device Name <span style={{ color: "red" }}>*</span>
            </Text>
            <Input
              value={deviceName}
              onChange={(e) =>
                updateDeviceState({ deviceName: e.target.value })
              }
              placeholder="Enter device name"
              style={{
                borderRadius: 8,
                fontWeight: 600,
                width: "100%",
                marginTop: 6,
              }}
            />
          </Col>
          <Col span={8}>
            <Text style={{ fontWeight: 600, color: "#232A36", fontSize: 13 }}>
              Device Model <span style={{ color: "red" }}>*</span>
            </Text>
            <Input
              value={deviceModel}
              onChange={(e) =>
                updateDeviceState({ deviceModel: e.target.value })
              }
              placeholder="Enter device model"
              style={{
                borderRadius: 8,
                fontWeight: 600,
                width: "100%",
                marginTop: 6,
              }}
            />
          </Col>
          <Col span={8}>
            <Text style={{ fontWeight: 600, color: "#232A36", fontSize: 13 }}>
              Unique ID <span style={{ color: "red" }}>*</span>
            </Text>
            <Input
              value={uniqueId}
              onChange={(e) => updateDeviceState({ uniqueId: e.target.value })}
              placeholder="Enter unique ID"
              style={{
                borderRadius: 8,
                fontWeight: 600,
                width: "100%",
                marginTop: 6,
              }}
            />
          </Col>
        </Row>
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col span={20}>
            <Text style={{ fontWeight: 600, color: "#232A36", fontSize: 13 }}>
              Description
            </Text>
            <Input.TextArea
              value={description}
              onChange={(e) =>
                updateDeviceState({ description: e.target.value })
              }
              placeholder="Enter device description (optional)"
              rows={1}
              style={{
                borderRadius: 8,
                fontWeight: 600,
                width: "100%",
                marginTop: 6,
              }}
            />
          </Col>
          <Col span={4} style={{ display: "flex", alignItems: "flex-end" }}>
            <Button
              type="primary"
              onClick={handleAddDevice}
              style={{
                width: "100%",
                borderRadius: 8,
                fontWeight: 700,
              }}
            >
              Add Device
            </Button>
          </Col>
        </Row>

        {/* Device List */}
        {devices?.length ? (
          <Card
            style={{
              borderRadius: 15,
              border: "1px solid #E5E8EC",
              background: "#FAFAFA",
            }}
          >
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 12 }}
            >
              <Text style={{ fontWeight: 700, fontSize: 16, color: "#232A36" }}>
                Added Devices
              </Text>
              <Tag
                style={{
                  fontWeight: 700,
                  fontSize: 15,
                  background: "#E5E8EC",
                  borderRadius: 6,
                  padding: "4px 12px",
                  border: "none",
                }}
              >
                {devices.length} {`Device${devices.length > 1 ? "s" : ""}`}
              </Tag>
            </Row>
            <Table
              columns={columns}
              dataSource={devices}
              rowKey="uniqueId"
              pagination={false}
              size="small"
              scroll={{ y: 180 }}
              components={{
                header: {
                  cell: (props) => (
                    <th
                      {...props}
                      style={{ ...props.style, background: "#F0F0F0" }}
                    />
                  ),
                },
              }}
            />
          </Card>
        ) : null}
      </Modal>
    </>
  );
}

export default AddDevices;
